import { useEffect } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';

const TOUR_UI_DATA_ATTR = 'data-tour-ui';

/**
 * Hook for handling keyboard navigation in tour components
 *
 * Handles:
 * - Escape: Stop the tour
 * - ArrowRight/ArrowDown: Navigate to next step
 * - ArrowLeft/ArrowUp: Navigate to previous step
 *
 * Blocks keyboard navigation when focus is on non-tour interactive elements (inputs, textareas, etc.)
 * unless they're marked with data-tour-ui attribute.
 */
export const useTourKeyboardNavigation = (options: {
  isOpen: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  onStop: () => void;
  onNext: () => void;
  onPrevious: () => void;
}): void => {
  const { isOpen, hasNext, hasPrevious, onStop, onNext, onPrevious } = options;

  const handleKeyDown = useEventCallback((event: KeyboardEvent) => {
    if (event.target instanceof HTMLElement) {
      // Allow keyboard navigation when focus is on tour UI elements (marked with data-tour-ui)
      const isTourUI = event.target.closest(`[${TOUR_UI_DATA_ATTR}]`);
      // Block keyboard navigation for other interactive elements (form inputs, etc.) unless they're tour UI
      if (!isTourUI && event.target.closest('input, textarea, select, button')) {
        return;
      }
    }

    switch (event.key) {
      case 'Escape':
        onStop();
        event.preventDefault();
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        if (hasNext) {
          onNext();
          event.preventDefault();
        }
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        if (hasPrevious) {
          onPrevious();
          event.preventDefault();
        }
        break;
    }
  });

  useEffect(() => {
    if (!isOpen) return;

    const cleanup = withWindow(
      global => {
        global.addEventListener('keydown', handleKeyDown);
        return () => {
          global.removeEventListener('keydown', handleKeyDown);
        };
      },
      () => {},
    );

    return cleanup;
  }, [isOpen, hasNext, hasPrevious, handleKeyDown]);
};
